// Internal tracking utility for admin dashboard
// This tracks user interactions on the website

const STORAGE_KEY = 'site_tracking_data';
const MAX_DAYS_STORAGE = 90; // Keep data for 90 days

// Get all tracking data from localStorage
export const getTrackingData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading tracking data:', error);
    return [];
  }
};

// Save tracking data to localStorage
const saveTrackingData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving tracking data:', error);
  }
};

// Clean old data (older than MAX_DAYS_STORAGE)
const cleanOldData = () => {
  const data = getTrackingData();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - MAX_DAYS_STORAGE);
  
  const filteredData = data.filter(item => {
    const itemDate = new Date(item.timestamp);
    return itemDate >= cutoffDate;
  });
  
  if (filteredData.length !== data.length) {
    saveTrackingData(filteredData);
  }
};

// Add a tracking event
export const trackEvent = (eventType, eventData = {}) => {
  cleanOldData();
  
  const event = {
    id: Date.now() + Math.random(),
    timestamp: new Date().toISOString(),
    eventType,
    ...eventData
  };
  
  const data = getTrackingData();
  data.push(event);
  saveTrackingData(data);
};

// Track link clicks
export const trackLinkClick = (linkText, linkUrl, linkType = 'internal') => {
  trackEvent('link_click', {
    linkText,
    linkUrl,
    linkType,
    page: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

// Track form interactions
export const trackFormStart = (formId, formName) => {
  trackEvent('form_start', {
    formId,
    formName,
    page: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

// Track form field interactions
export const trackFormFieldFocus = (formId, fieldName) => {
  trackEvent('form_field_focus', {
    formId,
    fieldName,
    page: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

// Track form submission
export const trackFormSubmit = (formId, formName, success = true, fieldsFilled = 0) => {
  trackEvent('form_submit', {
    formId,
    formName,
    success,
    fieldsFilled,
    page: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

// Track form abandonment (when user leaves page with incomplete form)
export const trackFormAbandonment = (formId, formName, fieldsFilled = 0, timeSpent = 0) => {
  trackEvent('form_abandonment', {
    formId,
    formName,
    fieldsFilled,
    timeSpent,
    page: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

// Track blog views
export const trackBlogView = (blogSlug, blogTitle) => {
  trackEvent('blog_view', {
    blogSlug,
    blogTitle,
    page: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

// Track page views
export const trackPageView = (pagePath, pageTitle) => {
  trackEvent('page_view', {
    pagePath,
    pageTitle
  });
};

// Track button clicks
export const trackButtonClick = (buttonText, buttonId, context) => {
  trackEvent('button_click', {
    buttonText,
    buttonId,
    context,
    page: typeof window !== 'undefined' ? window.location.pathname : ''
  });
};

// Get filtered data by date range
export const getFilteredData = (startDate, endDate, eventTypes = []) => {
  const data = getTrackingData();
  const start = new Date(startDate);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999); // Include entire end date
  
  return data.filter(item => {
    const itemDate = new Date(item.timestamp);
    const dateMatch = itemDate >= start && itemDate <= end;
    const typeMatch = eventTypes.length === 0 || eventTypes.includes(item.eventType);
    return dateMatch && typeMatch;
  });
};

// Get statistics
export const getStatistics = (startDate, endDate) => {
  const data = getFilteredData(startDate, endDate);
  
  const stats = {
    totalEvents: data.length,
    linkClicks: {},
    formStarts: {},
    formSubmits: {},
    formAbandonments: {},
    blogViews: {},
    buttonClicks: {},
    pageViews: {},
    formDropOffRate: 0,
    totalFormStarts: 0,
    totalFormSubmits: 0,
    totalFormAbandonments: 0,
    totalBlogViews: 0,
    totalLinkClicks: 0
  };
  
  data.forEach(item => {
    if (!item || !item.eventType) return;
    
    switch (item.eventType) {
      case 'link_click':
        stats.totalLinkClicks++;
        const linkKey = item.linkText || item.linkUrl || 'Unknown';
        stats.linkClicks[linkKey] = (stats.linkClicks[linkKey] || 0) + 1;
        break;
        
      case 'form_start':
        stats.totalFormStarts++;
        const formStartKey = item.formName || item.formId || 'Unknown';
        stats.formStarts[formStartKey] = (stats.formStarts[formStartKey] || 0) + 1;
        break;
        
      case 'form_submit':
        stats.totalFormSubmits++;
        const formSubmitKey = item.formName || item.formId || 'Unknown';
        stats.formSubmits[formSubmitKey] = (stats.formSubmits[formSubmitKey] || 0) + 1;
        break;
        
      case 'form_abandonment':
        stats.totalFormAbandonments++;
        const formAbandonKey = item.formName || item.formId || 'Unknown';
        stats.formAbandonments[formAbandonKey] = (stats.formAbandonments[formAbandonKey] || 0) + 1;
        break;
        
      case 'blog_view':
        stats.totalBlogViews++;
        const blogKey = item.blogSlug || item.blogTitle || 'Unknown';
        stats.blogViews[blogKey] = (stats.blogViews[blogKey] || 0) + 1;
        break;
        
      case 'button_click':
        const buttonKey = item.buttonText || item.buttonId || 'Unknown';
        stats.buttonClicks[buttonKey] = (stats.buttonClicks[buttonKey] || 0) + 1;
        break;
        
      case 'page_view':
        const pageKey = item.pagePath || 'Unknown';
        stats.pageViews[pageKey] = (stats.pageViews[pageKey] || 0) + 1;
        break;
        
      default:
        // Unknown event type - just count it in total events
        break;
    }
  });
  
  // Calculate drop-off rate
  if (stats.totalFormStarts > 0) {
    stats.formDropOffRate = ((stats.totalFormAbandonments / stats.totalFormStarts) * 100).toFixed(2);
  }
  
  return stats;
};

// Get time series data for charts
export const getTimeSeriesData = (startDate, endDate, eventType) => {
  try {
    const data = getFilteredData(startDate, endDate, eventType ? [eventType] : []);
    
    // Group by date
    const grouped = {};
    if (Array.isArray(data)) {
      data.forEach(item => {
        if (item && item.timestamp) {
          try {
            const date = new Date(item.timestamp).toISOString().split('T')[0];
            if (date && date.length === 10) {
              grouped[date] = (grouped[date] || 0) + 1;
            }
          } catch (e) {
            // Skip invalid timestamps
          }
        }
      });
    }
    
    // Convert to array format for charts
    const dates = [];
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // Ensure valid dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return [];
      }
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        try {
          const dateStr = d.toISOString().split('T')[0];
          const formattedDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          if (dateStr && formattedDate) {
            dates.push({
              date: dateStr,
              count: Number(grouped[dateStr] || 0),
              formattedDate: formattedDate
            });
          }
        } catch (e) {
          // Skip invalid dates
          continue;
        }
      }
    } catch (e) {
      console.error('Error generating date series:', e);
      return [];
    }
    
    return dates;
  } catch (error) {
    console.error('Error in getTimeSeriesData:', error);
    return [];
  }
};

// Export data to CSV
export const exportToCSV = (startDate, endDate, eventTypes = []) => {
  const data = getFilteredData(startDate, endDate, eventTypes);
  
  if (data.length === 0) {
    return null;
  }
  
  // Get all unique keys from all events
  const allKeys = new Set();
  data.forEach(item => {
    Object.keys(item).forEach(key => allKeys.add(key));
  });
  
  const headers = Array.from(allKeys);
  
  // Create CSV content
  const csvRows = [headers.join(',')];
  
  data.forEach(item => {
    const row = headers.map(header => {
      const value = item[header];
      // Escape commas and quotes in CSV
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
};

